// Route search over the merged nav graph (see graph.js for the data model).
//
// The one rule this module is built on: an edge may change floor AND building
// (outdoor links, tunnels, skywalks). The only edges that are floor *switches*
// for the user — where they physically take stairs or an elevator — are edges
// whose two endpoints are shaft nodes of the SAME type (stair↔stair or
// elevator↔elevator) on different floors. Everything else (a door from outdoor
// floor 0 into floor 2, a tunnel from F1 of one building to F2 of another) is
// ordinary walking; the map just re-renders that building at the new floor.

import { haversineMeters } from './geo.js';
import { SHAFT_TYPES } from './graph.js';

// Cost tuning, in meter-equivalents.
//
// Shaft penalties are charged PER SHAFT USE, not per floor travelled: half on
// each same-floor edge that touches the shaft node (walk in + walk out = one
// full penalty), while the cross-floor ride edges only add a small per-floor
// cost. Riding one stairwell F1→F3 therefore beats splitting the climb across
// two different stairwells/buildings — routes use as few distinct shafts as
// possible.
export const STAIR_PENALTY = 8;       // per stairwell used
export const STAIR_FLOOR_COST = 6;    // per floor climbed within it
export const ELEVATOR_PENALTY = 45;   // per elevator used (wait time)
export const ELEVATOR_FLOOR_COST = 3; // per floor ridden within it
// Crossing between buildings (including stepping out to `outdoors`) costs
// extra, so routes stay inside one building instead of hopping out, cutting
// through a neighbour, and back in. Tunnel edges are exempt — a tunnel link
// between buildings IS the preferred way to change buildings.
export const BUILDING_CHANGE_PENALTY = 25;
// Walking THROUGH a door — in one end of a room and out the other — is charged
// like a shaft use: half on each same-floor edge touching a door node, so a
// door passed through pays the full penalty while a door merely started or
// finished at (the room you are in, the room you are going to) pays half. That
// half is constant across every candidate door of a room, so it cannot change
// which entrance the search settles on.
//
// Without this a door costs nothing, and a corridor drawn as a room — Fletcher
// Argue's 105cor, doored at both ends — wins on raw metres by half a metre and
// sends people through two doors to save a step.
// 10 is the knee of the curve, measured over 400 random room→room routes under
// 600 m: door transits fall 0.70 → 0.29 per route for ~1 m of extra walking on
// average, and raising it further removes almost nothing (0.26 at 50) while
// growing the worst-case detour. The doors left at 10 are the necessary ones.
export const DOOR_PENALTY = 10;   // per door walked through
// Tunnels toggle: going outside is to be avoided at ALL costs. Every outdoor
// meter costs ×OUTDOOR_FACTOR (so Dijkstra effectively minimizes outdoor
// distance first, total distance second) and every step through an exit node
// to the outside adds a flat penalty — even a 2 m outdoor shortcut loses to a
// long indoor/tunnel detour. Outdoor routes remain the fallback when no
// indoor path exists at all.
export const OUTDOOR_FACTOR = 1000;
export const EXIT_USE_PENALTY = 300;  // per exit crossed while Tunnels is on

// Every knob of the cost model in one place. findRoute accepts overrides via
// options.costs (the Pathfinder Tuner passes its live values through this).
export const DEFAULT_COSTS = {
  stairPenalty: STAIR_PENALTY,
  stairFloorCost: STAIR_FLOOR_COST,
  elevatorPenalty: ELEVATOR_PENALTY,
  elevatorFloorCost: ELEVATOR_FLOOR_COST,
  buildingChangePenalty: BUILDING_CHANGE_PENALTY,
  doorPenalty: DOOR_PENALTY,
  outdoorFactor: OUTDOOR_FACTOR,       // tunnels toggle only
  exitUsePenalty: EXIT_USE_PENALTY,    // tunnels toggle only
  baseOutdoorFactor: 1,                // outdoor meters multiplier, always applied
};

// How the engine reads a node's type. findRoute swaps in an override-aware
// reader when `options.nodeTypes` is given, so a retyped node is retyped for
// every question asked below — this is the ONLY place `node.type` is read.
const nodeType = (node) => node?.type;

/**
 * 'stair' | 'elevator' if this edge is a floor switch, else null.
 * `typeOf` reads a node's type; findRoute passes its override-aware reader.
 */
export function transitionKind(edge, nodes, typeOf = nodeType) {
  const a = nodes.get(edge.from), b = nodes.get(edge.to);
  if (!a || !b || a.floor === b.floor) return null;
  const ta = typeOf(a);
  if (ta !== typeOf(b) || !SHAFT_TYPES.has(ta)) return null;
  return ta;
}

/**
 * Dijkstra from `fromId` to `toId`. Each may be a single node id OR an array
 * of node ids — a room with several entrances (possibly on different floors)
 * passes all its door nodes, and the search settles on the pair giving the
 * cheapest route, i.e. the nearest usable entrance at each end.
 * options: { accessible, tunnels, noElevators, noTunnels, costs,
 *            excludeNodes, excludeEdges, nodeTypes } — all optional.
 *   accessible:   hard-blocks stair transitions and any edge touching a
 *                 'non-accessible' node (steps in a hallway etc.).
 *   noElevators:  hard-blocks elevator transitions.
 *   tunnels:      soft-penalizes outdoor edges (see OUTDOOR_FACTOR).
 *   noTunnels:    hard-blocks tunnel edges and any edge touching a node of
 *                 the `tunnels` pseudo-building.
 *   costs:        partial DEFAULT_COSTS override (Pathfinder Tuner).
 *   excludeNodes: Set<nodeId> — every edge touching one is refused, and a
 *                 listed start/end id is dropped from the candidates, so a
 *                 blocked start (or end) has no route. (Route tester edits.)
 *   excludeEdges: Set<edgeId> — refused by id; the nodes at either end stay,
 *                 so cutting a shaft's floor links keeps its landings walkable.
 *   nodeTypes:    Map<nodeId, type> — type overrides read everywhere this
 *                 function asks a node's type (transitions, the accessible
 *                 refusal, the per-node penalties). Untouched nodes keep their
 *                 own type. 'non-accessible' only bites with `accessible`.
 * None of these touch `mapData`; they are overlays on one search.
 *
 * Returns null when unreachable, else:
 * {
 *   nodes, edges,                    // full ordered path
 *   legs: [{ nodes, edges }],        // split at shaft transitions only
 *   transitions: [{ kind, fromFloor, toFloor, fromNode, toNode }], // transitions[i] joins legs[i] → legs[i+1]
 *   totalMeters, outdoorMeters, tunnelMeters, cost,
 *   fromId, toId                     // the specific endpoints actually used
 * }
 */
export function findRoute(mapData, fromId, toId, options = {}) {
  const { nodes, adjacency } = mapData;
  const {
    accessible = false, tunnels = false, noElevators = false, noTunnels = false,
    excludeNodes = null, excludeEdges = null, nodeTypes = null,
  } = options;
  const blocked = (id) => excludeNodes != null && excludeNodes.has(id);
  const typeOf = nodeTypes != null && nodeTypes.size > 0
    ? (node) => (node ? (nodeTypes.get(node.id) ?? node.type) : undefined)
    : nodeType;
  const sources = [...new Set((Array.isArray(fromId) ? fromId : [fromId]).filter(id => nodes.has(id) && !blocked(id)))];
  const targets = new Set((Array.isArray(toId) ? toId : [toId]).filter(id => nodes.has(id) && !blocked(id)));
  if (sources.length === 0 || targets.size === 0) return null;
  const C = { ...DEFAULT_COSTS, ...(options.costs || {}) };

  const edgeAllowed = (edge) => {
    if (excludeEdges != null && excludeEdges.has(edge.id)) return false;
    if (blocked(edge.from) || blocked(edge.to)) return false;
    const kind = transitionKind(edge, nodes, typeOf);
    if (accessible && kind === 'stair') return false;
    if (noElevators && kind === 'elevator') return false;
    if (accessible && (typeOf(nodes.get(edge.from)) === 'non-accessible' || typeOf(nodes.get(edge.to)) === 'non-accessible')) return false;
    // "No tunnels": drop tunnel edges and any edge touching a tunnel node.
    if (noTunnels) {
      if (edge.tunnel) return false;
      if (nodes.get(edge.from)?.building === 'tunnels' || nodes.get(edge.to)?.building === 'tunnels') return false;
    }
    return true;
  };

  const edgeCost = (edge) => {
    const a = nodes.get(edge.from), b = nodes.get(edge.to);
    let cost = haversineMeters(a, b) * (edge.weight || 1);
    // Outdoor exposure: the explicit flag, or either end being a floor-0
    // (outdoor pseudo-building) node.
    const isOutdoor = edge.outdoor || a.floor === 0 || b.floor === 0;
    if (isOutdoor) cost *= tunnels ? C.outdoorFactor : C.baseOutdoorFactor;
    const kind = transitionKind(edge, nodes, typeOf);
    if (kind === 'stair') cost += C.stairFloorCost * Math.abs(a.floor - b.floor);
    else if (kind === 'elevator') cost += C.elevatorFloorCost * Math.abs(a.floor - b.floor);
    else {
      // Per-shaft-use penalty: half on each same-floor edge touching a shaft
      // node, so entering + leaving a stairwell/elevator = one full penalty
      // regardless of how many floors are ridden inside it. An edge joining
      // two shaft nodes charges both halves (leave one, enter the other).
      for (const n of [a, b]) {
        const t = typeOf(n);
        if (t === 'stair') cost += C.stairPenalty / 2;
        else if (t === 'elevator') cost += C.elevatorPenalty / 2;
        else if (t === 'door') cost += C.doorPenalty / 2;
      }
    }
    if (a.building !== b.building && !edge.tunnel) {
      cost += C.buildingChangePenalty;
      // Tunnels mode: stepping outside through an exit is what we're avoiding.
      if (tunnels && (typeOf(a) === 'exit' || typeOf(b) === 'exit') && isOutdoor) cost += C.exitUsePenalty;
    }
    return cost;
  };

  // Multi-source Dijkstra with a binary min-heap keyed on tentative cost: every
  // source starts at distance 0, and the first target popped is the nearest
  // reachable entrance. `prev` has no entry for source nodes, so path
  // reconstruction stops when it reaches one.
  const dist = new Map();
  const prev = new Map(); // nodeId → { nodeId, edge }
  const done = new Set();
  const heap = new MinHeap();
  for (const s of sources) { dist.set(s, 0); heap.push(0, s); }

  let reached = null;
  while (heap.size > 0) {
    const { key: d, value: id } = heap.pop();
    if (done.has(id)) continue;
    done.add(id);
    if (targets.has(id)) { reached = id; break; }
    for (const { edge, other } of adjacency.get(id) || []) {
      if (done.has(other) || !edgeAllowed(edge)) continue;
      const nd = d + edgeCost(edge);
      if (nd < (dist.get(other) ?? Infinity)) {
        dist.set(other, nd);
        prev.set(other, { nodeId: id, edge });
        heap.push(nd, other);
      }
    }
  }
  if (reached == null) return null;

  // ── Reconstruct path ──────────────────────────────────────────────────────
  const pathNodes = [];
  const pathEdges = [];
  let cur = reached;
  while (prev.has(cur)) {
    const p = prev.get(cur);
    pathNodes.unshift(nodes.get(cur));
    pathEdges.unshift(p.edge);
    cur = p.nodeId;
  }
  pathNodes.unshift(nodes.get(cur)); // cur is the source we started from
  const usedFromId = cur, usedToId = reached;
  if (pathEdges.length === 0) return null; // from and to resolve to the same node

  // ── Split into legs at shaft transitions ──────────────────────────────────
  const legs = [{ nodes: [pathNodes[0]], edges: [] }];
  const transitions = [];
  let totalMeters = 0, outdoorMeters = 0, tunnelMeters = 0;
  for (let i = 0; i < pathEdges.length; i++) {
    const edge = pathEdges[i];
    const a = pathNodes[i], b = pathNodes[i + 1];
    const meters = haversineMeters(a, b);
    totalMeters += meters;
    if (edge.outdoor) outdoorMeters += meters;
    if (edge.tunnel) tunnelMeters += meters;
    const kind = transitionKind(edge, nodes, typeOf);
    if (kind) {
      transitions.push({ kind, fromFloor: a.floor, toFloor: b.floor, fromNode: a, toNode: b });
      legs.push({ nodes: [b], edges: [] });
    } else {
      const leg = legs[legs.length - 1];
      leg.nodes.push(b);
      leg.edges.push(edge);
    }
  }

  // ── Collapse multi-floor rides in one shaft ────────────────────────────────
  // Going F3→F1 in the same stairwell is reconstructed as two transitions
  // (F3→F2, F2→F1) with a zero-walk pass-through leg between them. To the user
  // that's a single "take the stairs from F3 to F1", so merge consecutive
  // same-type transitions when the leg between them has no walking edges, the
  // shaft continues from the same node, and the floor direction is monotonic.
  for (let i = 0; i < transitions.length - 1; ) {
    const t = transitions[i], u = transitions[i + 1];
    const middle = legs[i + 1]; // leg entered by t, exited by u
    const passThrough = middle.edges.length === 0;
    const sameShaft = t.kind === u.kind && t.toNode.id === u.fromNode.id;
    const dir1 = Math.sign(t.toFloor - t.fromFloor);
    const dir2 = Math.sign(u.toFloor - u.fromFloor);
    if (passThrough && sameShaft && dir1 === dir2 && dir1 !== 0) {
      t.toFloor = u.toFloor;
      t.toNode = u.toNode;
      transitions.splice(i + 1, 1);
      // Drop the pass-through leg; the next leg becomes the destination of t.
      legs.splice(i + 1, 1);
    } else {
      i++;
    }
  }

  return {
    nodes: pathNodes, edges: pathEdges, legs, transitions,
    totalMeters, outdoorMeters, tunnelMeters, cost: dist.get(reached),
    fromId: usedFromId, toId: usedToId,
  };
}

/**
 * Which floor each building should display while a given leg is active:
 * Map<buildingSlug, floor>. Within one leg a building's floor is unique
 * (floors only change through shaft transitions, which split legs), so the
 * first node seen per building wins. Outdoor (floor 0) nodes are skipped.
 */
export function legFloors(leg) {
  const byBuilding = new Map();
  for (const n of leg.nodes) {
    if (n.floor === 0 || byBuilding.has(n.building)) continue;
    byBuilding.set(n.building, n.floor);
  }
  return byBuilding;
}

class MinHeap {
  constructor() { this.items = []; }
  get size() { return this.items.length; }
  push(key, value) {
    const items = this.items;
    items.push({ key, value });
    let i = items.length - 1;
    while (i > 0) {
      const p = (i - 1) >> 1;
      if (items[p].key <= items[i].key) break;
      [items[p], items[i]] = [items[i], items[p]];
      i = p;
    }
  }
  pop() {
    const items = this.items;
    const top = items[0];
    const last = items.pop();
    if (items.length > 0) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const l = 2 * i + 1, r = l + 1;
        let m = i;
        if (l < items.length && items[l].key < items[m].key) m = l;
        if (r < items.length && items[r].key < items[m].key) m = r;
        if (m === i) break;
        [items[m], items[i]] = [items[i], items[m]];
        i = m;
      }
    }
    return top;
  }
}
